import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTour } from '../contexts/TourContext';
import { useLanguage } from '../contexts/LanguageContext';

interface TourStep {
  title: string;
  description: string;
  path: string;
  element?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  highlightElement?: string;
  spotlightRadius?: number;
}

const tourSteps: TourStep[] = [
  // Home Page Steps
  {
    title: "Welcome to .NET Mastery",
    description: "Let's take a quick tour of our platform's features to help you get started.",
    path: "/",
    position: "bottom",
    highlightElement: ".hero-section"
  },
  {
    title: "Navigation Menu",
    description: "Use these links to access different sections of the platform.",
    path: "/",
    position: "bottom",
    highlightElement: "nav",
    spotlightRadius: 200
  },
  
  // Quiz Page Steps
  {
    title: "Quiz Section",
    description: "This is where you can test your .NET knowledge.",
    path: "/quiz",
    position: "top",
    highlightElement: "#quiz-section"
  },
  {
    title: "Quiz Controls",
    description: "Select your answers and use these buttons to navigate through questions.",
    path: "/quiz",
    position: "bottom",
    highlightElement: ".quiz-controls"
  },
  
  // Coding Challenge Steps
  {
    title: "Code Editor",
    description: "Write and test your C# code in this interactive editor.",
    path: "/coding-challenge",
    position: "left",
    highlightElement: "#monaco-editor"
  },
  {
    title: "Test Cases",
    description: "View test cases and run your code to check the output.",
    path: "/coding-challenge",
    position: "right",
    highlightElement: ".test-cases"
  },
  {
    title: "AI Assistant",
    description: "Get help and feedback from our AI coding assistant.",
    path: "/coding-challenge",
    position: "bottom",
    highlightElement: "#ai-assistant"
  },
  
  // Interview Practice Steps
  {
    title: "Interview Simulator",
    description: "Practice technical interviews with our AI interviewer.",
    path: "/interviews",
    position: "top",
    highlightElement: "#interview-simulator"
  },
  {
    title: "Interview Settings",
    description: "Customize your interview experience based on your needs.",
    path: "/interviews",
    position: "right",
    highlightElement: ".interview-settings"
  },
  
  // References Page
  {
    title: "Learning Resources",
    description: "Access curated .NET learning materials and documentation.",
    path: "/references",
    position: "top",
    highlightElement: "#resources-grid"
  }
];

export default function GuidedTour() {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const { isTourActive, endTour } = useTour();
  const { t } = useLanguage();

  useEffect(() => {
    if (isTourActive && currentStep < tourSteps.length) {
      const currentElement = tourSteps[currentStep].highlightElement;
      if (currentElement) {
        highlightElement(currentElement);
      }
    }

    return () => {
      removeHighlight();
    };
  }, [currentStep, isTourActive]);

  const highlightElement = (selector: string) => {
    const element = document.querySelector(selector);
    if (element) {
      element.classList.add('tour-highlight');
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const removeHighlight = () => {
    const highlighted = document.querySelector('.tour-highlight');
    if (highlighted) {
      highlighted.classList.remove('tour-highlight');
    }
  };

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      removeHighlight();
      setCurrentStep(prev => prev + 1);
      navigate(tourSteps[currentStep + 1].path);
    } else {
      completeTour();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      removeHighlight();
      setCurrentStep(prev => prev - 1);
      navigate(tourSteps[currentStep - 1].path);
    }
  };

  const completeTour = () => {
    removeHighlight();
    endTour();
    navigate('/');
  };

  if (!isTourActive) return null;

  const currentTourStep = tourSteps[currentStep];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className={`fixed transform -translate-x-1/2 z-50 ${
          currentTourStep.position === 'top' ? 'top-8' : 'bottom-8'
        } ${
          currentTourStep.position === 'left' ? 'left-8 translate-x-0' : 
          currentTourStep.position === 'right' ? 'right-8 translate-x-0' : 'left-1/2'
        }`}
      >
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md border border-[#512BD4]/10">
          <button
            onClick={completeTour}
            className="absolute top-4 right-4 text-gray-400 hover:text-[#512BD4] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="mb-4">
            <h3 className="text-xl font-semibold text-[#512BD4] mb-2">
              {currentTourStep.title}
            </h3>
            <p className="text-gray-600">{currentTourStep.description}</p>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    index === currentStep 
                      ? 'bg-[#512BD4]' 
                      : 'bg-gray-200 hover:bg-[#512BD4]/30'
                  }`}
                />
              ))}
            </div>

            <div className="flex space-x-4">
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className="flex items-center text-gray-600 hover:text-[#512BD4] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center bg-gradient-to-r from-[#512BD4] to-[#6B42E7] text-white px-4 py-2 rounded-lg hover:from-[#6B42E7] hover:to-[#512BD4] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
                {currentStep < tourSteps.length - 1 && (
                  <ChevronRight className="w-5 h-5 ml-1" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}